import { DatePicker, TimeRangePickerProps } from 'antd'
import moment from 'moment'
import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import { DateScope } from '@/components/QueryWidgets/RangeDatePickerV2'
import { Merge } from '@/utils/utilityTypes'

import styles from './index.less'

const { RangePicker } = DatePicker

/** 日期时间选择器值 */
export interface DateRangeValueType {
    startTime?: string
    endTime?: string
    presetDateRangeType?: string
}

export interface RangePickerExtendProps {
    value?: DateRangeValueType
    onChange: (value: DateRangeValueType) => void
    label?: ReactNode
    format?: string
    startFormat?: string
    endFormat?: string
    showTime?: boolean
    rangeDays?: number
    allowClear?: boolean
    /** 是否禁用未来日期 */
    disableFutureDate?: boolean
    /**
     * 禁用未来日期的偏移量，仅在 `disableFutureDate` 启用时生效
     * 基于当天计算偏移量，为 0 时则从当天开始禁用，为 1 时则从一天后开始禁用，以此类推
     */
    disableFutureDateOffset?: number
    /** 快捷选择时间范围 */
    quick?: boolean
    /** 快捷选择配置 */
    scopeConfig?: any
}

export type DateRangeProps = Merge<TimeRangePickerProps, RangePickerExtendProps>

export default (props: DateRangeProps) => {
    const {
        value,
        onChange,
        label,
        format = 'YYYY-MM-DD HH:mm:ss',
        startFormat,
        endFormat,
        showTime = true,
        rangeDays = false,
        allowClear = true,
        disableFutureDate = false,
        disableFutureDateOffset = 0,
        quick = true,
        scopeConfig,
        ...restProps
    } = props

    const [dates, setDates] = useState([])
    const [open, setOpen] = useState(false)
    const disabledDate = current => {
        if (!rangeDays) {
            return false
        }
        if (!dates) {
            return false
        }

        if (disableFutureDate) {
            const offset = disableFutureDateOffset
            const today = moment().startOf('day')

            if (current.isAfter(today.add(offset, 'days'))) {
                return true
            }
        }

        const tooLate = dates[0] && current.diff(dates[0], 'days') > rangeDays
        const tooEarly = dates[1] && dates[1].diff(current, 'days') > rangeDays
        return tooEarly || tooLate
    }
    const onDateScopeChange = scopeItem => {
        const [start, end] = scopeItem.getRangeValue?.() || []
        onChange({
            startTime: start ? start.format(startFormat || format) : undefined,
            endTime: end ? end.format(endFormat || format) : undefined,
            presetDateRangeType: scopeItem.value
        })
        setOpen(false)
    }

    useEffect(() => {
        if (!rangeDays) {
            return
        }
        if (open) {
            setDates([null, null])
        } else {
            setDates(null)
        }
    }, [open, rangeDays])

    const dateValue = useMemo(() => {
        if (rangeDays && dates) {
            return dates
        }
        return value
            ? [
                  value.startTime && moment(value.startTime, startFormat || format),
                  value.endTime && moment(value.endTime, endFormat || format)
              ]
            : undefined
    }, [dates, endFormat, format, rangeDays, startFormat, value])

    return (
        <div className={styles.container} {...restProps}>
            {label ? <span className={styles.label}>{label}</span> : null}
            <RangePicker
                allowClear={allowClear}
                value={dateValue}
                showTime={showTime}
                onChange={value => {
                    if (!value) {
                        onChange(undefined)
                        return
                    }

                    const [start, end] = value
                    onChange({
                        startTime: start ? start.format(startFormat || format) : undefined,
                        endTime: end ? end.format(endFormat || format) : undefined
                    })
                }}
                disabledDate={disabledDate}
                onCalendarChange={val => setDates(val)}
                open={open}
                onOpenChange={() => {
                    setOpen(!open)
                }}
                renderExtraFooter={
                    quick
                        ? () => {
                              return <DateScope scopeConfig={scopeConfig} onChange={onDateScopeChange} />
                          }
                        : undefined
                }
                {...restProps}
            />
        </div>
    )
}
